import { Component, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';

import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

import { AuthService } from './core/services/auth.service';
import { SocketService } from './core/services/socket.service';
import { NotificationService } from './shared/services/notification.service';
import { LayoutComponent } from './layout/layout.component';
import { TranslocoService } from '@ngneat/transloco';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    ToastModule,
    LayoutComponent
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit {
  title = 'Print Portal';

  constructor(
    public authService: AuthService,
    private socketService: SocketService,
    private notificationService: NotificationService,
    private translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    // Initialize socket connection if user is authenticated
    if (this.authService.isAuthenticated()) {
      this.setupSocketListeners();
    }

    // Listen for authentication changes
    this.authService.isAuthenticated$.subscribe(isAuth => {
      if (isAuth) {
        this.setupSocketListeners();
      }
    });
  }

  private setupSocketListeners(): void {
    // Listen for new orders
    this.socketService.onNewOrder().subscribe(data => {
      this.notificationService.showInfo(
        this.translocoService.translate('notifications.newOrder'),
        this.translocoService.translate('notifications.newOrderDetail', {
          orderNumber: data.orderNumber,
          customer: data.customer
        })
      );
    });

    // Listen for file uploads
    this.socketService.onFileUploaded().subscribe(data => {
      this.notificationService.showInfo(
        this.translocoService.translate('notifications.fileUploaded'),
        this.translocoService.translate('notifications.fileUploadedDetail', {
          uploader: data.uploader,
          fileName: data.fileName
        })
      );
    });

    // Listen for order status changes
    this.socketService.onOrderStatusChanged().subscribe(data => {
      // Use the message from backend if available, otherwise construct from status
      const message = data.message || this.translocoService.translate('notifications.orderStatusChangedDetail', {
        orderNumber: data.orderNumber,
        status: this.translocoService.translate(`orderStatus.${data.status}`)
      });

      this.notificationService.showInfo(
        this.translocoService.translate('notifications.orderStatusChanged'),
        message
      );
    });

    // Listen for order reviews
    this.socketService.onOrderReviewed().subscribe(data => {
      const messageKey = data.status === 'approved' ?
        'notifications.orderFullyApproved' :
        'notifications.orderPartiallyRejected';

      const message = this.translocoService.translate(messageKey, {
        orderNumber: data.orderNumber,
        approvedPages: data.approvedPages,
        rejectedPages: data.rejectedPages
      });

      this.notificationService.showInfo(
        this.translocoService.translate('notifications.orderReviewed'),
        message
      );
    });

    // Listen for order cancellations
    this.socketService.onOrderCancelled().subscribe(data => {
      this.notificationService.showWarning(
        this.translocoService.translate('notifications.orderCancelled'),
        this.translocoService.translate('notifications.orderCancelledDetail', {
          orderNumber: data.orderNumber
        })
      );
    });
  }
}
